import React, { useEffect } from 'react';
import { Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

import { useAuth0 } from '../contexts/useAuthContext';
import SidebarLayout from '../layout/Layout';
import PublicLayout from '../layout/PublicLayout';
import LoginForm from '../pages/login/Login';
import AuthCallback from '../authentication/Callback';
import Home from '../pages/home/Home';
import Manage from '../pages/manage/Manage';
import Buildings from '../pages/manage/Buildings';
import CommsDetails from '../pages/comms/CommsDetails';
import Dashboard from '../pages/dashboard/Dashboard';
import DistributionGroups from '../pages/manage/DistributionGroups';
import Schedules from '../pages/manage/Schedules';
import Pending from '../pages/manage/Pending';
import Documentation from '../pages/documentation/Documentation';
import AlarmDetails from '../pages/alarms/AlarmDetails';
import ReportsDetails from '../pages/reports/ReportsDetails';
import SiteDetails from '../pages/sites/SiteDetails';
import RulesDetails from '../pages/manage/Rules';
import CustomerDetails from '../pages/customers/CustomerDetails';
import VirtualTenantsDetails from '../pages/virtualTenants/VirtualTenantsDetails';
import CSHDetails from '../pages/CSHDetails/CSHDetails';
import Admin from '../pages/admin/AdminUsers';
import ProtectedRoute from './ProtectedRoute';
import AttachmentGroups from '../pages/manage/AttachmentGroups';
import { AuthLoadingMessage } from '../components/ui';
import AccessRequestAdmin from '../pages/admin/AdminAccessRequest';
import AdminUserActivity from '../pages/admin/AdminUserActivity';
import RequestAccess from '../pages/requestAccess/RequestAccess';
import { Help } from '../pages/help/Help';
import NotFound from '../pages/errors/404';
import CSHGroups from '../pages/manage/CshGroups';
import Categories from '../pages/manage/Categories';
import AlarmEstateView from '../pages/alarmEstate/AlarmEstateView';

function AppRoutes() {
  const { isAuthenticated, isLoading, error, authPhase, isNormalUser } = useAuth0();
  const navigate = useNavigate();

  // Debug: allow forcing the loading screen via ?debugLoading
  const debugLoading = import.meta.env.DEV && new URLSearchParams(globalThis.location.search).has('debugLoading');

  // Call hooks in the same order.
  useEffect(() => {
    if (debugLoading) return;
    if (!isLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, debugLoading]);
  
  // Redirect Normal users to request-access if they try to access protected pages
  useEffect(() => {
    if (!isLoading && isAuthenticated && isNormalUser()) {
      const path = globalThis.location.pathname;
      if (path !== '/request-access' && path !== '/login' && path !== '/callback' && path !== '/sb/callback') {
        navigate('/request-access', { replace: true });
      }
    }
  }, [isAuthenticated, isLoading, isNormalUser, navigate]);

  if (debugLoading) {
    return <AuthLoadingMessage phase={authPhase} />;
  }

  if (isLoading) {
    return <AuthLoadingMessage phase={authPhase} />;
  }

  // If an auth error occurs (and we're not intentionally signing out),
  // immediately redirect to the login page instead of rendering the error UI.
  // This prevents briefly showing the auth error message before the post-logout
  // fallback redirect occurs.
  if (error && authPhase !== 'signingOut') {
    console.info('Authentication error:', error);
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
      {/* Public routes with navbar */}
      <Route element={<PublicLayout />}>
        <Route path="/login" element={<LoginForm />} />
        <Route path="/callback" element={<AuthCallback />} />
        <Route path="/sb/callback" element={<AuthCallback />} />
      </Route>
      
      {/* Public routes with sidebar layout (no auth required) */}
      <Route element={<SidebarLayout />}>
        <Route path="/request-access" element={<RequestAccess />} />
        <Route path="/help" element={<Help />} />
      </Route>
      
      {/* Authenticated routes with full layout */}
      {isAuthenticated ? (
        <Route element={<SidebarLayout />}>
          <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
          <Route path="/alarm/edit/:id" element={<ProtectedRoute><AlarmDetails /></ProtectedRoute>} />
          <Route path="/sites/:id" element={<ProtectedRoute><SiteDetails /></ProtectedRoute>} />
          <Route path="/customers/:id" element={<ProtectedRoute><CustomerDetails /></ProtectedRoute>} />
          <Route path="/csh-details/:id" element={<ProtectedRoute><CSHDetails /></ProtectedRoute>} />
          <Route path="/virtualTenants/vt/:id" element={<ProtectedRoute><VirtualTenantsDetails /></ProtectedRoute>} />
          <Route path="/alarm-estate" element={<ProtectedRoute><AlarmEstateView /></ProtectedRoute>} />
          <Route path="/manage" element={<ProtectedRoute><Manage /></ProtectedRoute>} />
          <Route path="/manage/buildings" element={<ProtectedRoute><Buildings /></ProtectedRoute>} />
          <Route path="/manage/distribution-groups" element={<ProtectedRoute><DistributionGroups /></ProtectedRoute>} />
          <Route path="/manage/attachment-groups" element={<ProtectedRoute><AttachmentGroups /></ProtectedRoute>} />
          <Route path="/manage/csh-groups" element={<ProtectedRoute><CSHGroups /></ProtectedRoute>} />
          <Route path="/manage/categories" element={<ProtectedRoute><Categories /></ProtectedRoute>} />

          <Route path="/manage/schedules" element={<ProtectedRoute><Schedules /></ProtectedRoute>} />
          <Route path="/manage/rules" element={<ProtectedRoute><RulesDetails /></ProtectedRoute>} />
          <Route path="/manage/pending" element={<ProtectedRoute><Pending /></ProtectedRoute>} />
          <Route path="/comms" element={<ProtectedRoute><CommsDetails /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><ReportsDetails /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute requireAdmin={true}><Admin /></ProtectedRoute>} />
          <Route path="/documentation" element={<ProtectedRoute><Documentation /></ProtectedRoute>} />
          <Route path="/admin/request" element={<ProtectedRoute requireAdmin={true}><AccessRequestAdmin /></ProtectedRoute>} />
          <Route path="/admin/user-activity" element={<ProtectedRoute requireAdmin={true}><AdminUserActivity /></ProtectedRoute>} />
          <Route path="*" element={<NotFound />} />
        </Route>
      ) : (
        <>
          <Route path="*" element={<Navigate to="/login" replace />} />
        </>
      )}
    </Routes>
    </>
  );
}

export default AppRoutes;
